import { useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { apiClient, getErrorMessage } from '../../api/client'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { resolveTenantSlug } from '../../lib/tenant'

type TreatmentTarget = {
  id: number
  target_number: number
  target_description: string
  measurement_type_display: string
  mastery_threshold: number
  mastery_consecutive_sessions: number
  current_phase_display: string
  is_active: boolean
  mastered_date: string | null
}

type TreatmentGoal = {
  id: number
  goal_number: number
  domain: string
  goal_description: string
  baseline: string
  mastery_criteria: string
  service_code: string
  status: string
  status_display: string
  targets: TreatmentTarget[]
}

type BehaviorDefinition = {
  id: number
  behavior_name: string
  operational_definition: string
  topography: string | null
  examples: string | null
  non_examples: string | null
  measurement_type_display: string
  is_target_behavior: boolean
}

type Attachment = {
  id: number
  kind: string
  kind_display: string
  title: string
  description: string | null
  file_name: string
  file_size: number
  mime_type: string | null
  uploaded_by_name: string | null
  uploaded_at: string
  download_url: string | null
}

type TreatmentPlanDetail = {
  id: number
  client: number
  client_name: string
  version_number: number
  effective_date: string
  end_date: string | null
  review_date: string
  status: string
  status_display: string
  is_active: boolean
  presenting_concerns: string
  clinical_summary: string
  treatment_recommendations: string
  recommended_hours_per_week: number
  recommended_supervision_hours: number
  recommended_caregiver_training_hours: number
  primary_diagnosis_code: string
  primary_diagnosis_description: string
  goals: TreatmentGoal[]
  behavior_definitions: BehaviorDefinition[]
  attachments: Attachment[]
  created_by_name: string
  approved_by_name: string | null
  approved_at: string | null
  created_at: string
  updated_at: string
}

export default function TreatmentPlanDetailPage() {
  const tenantSlug = resolveTenantSlug()
  const params = useParams()
  const planId = Number(params.planId)

  const cacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:treatment_plans:detail:${planId}`
  }, [planId, tenantSlug])

  const query = useOfflineFirstQuery<TreatmentPlanDetail>({
    queryKey: ['treatment-plan', planId],
    cacheKey,
    cacheMaxAgeMs: 10 * 60 * 1000,
    enabled: Number.isFinite(planId) && planId > 0,
    queryFn: async () => {
      const res = await apiClient.get<TreatmentPlanDetail>(`/api/v1/treatment-plans/${planId}/`)
      return res.data
    },
  })

  if (!Number.isFinite(planId) || planId <= 0) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Invalid treatment plan id.
      </div>
    )
  }

  const plan = query.data

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Treatment Plan</h1>
          <p className="text-sm text-gray-600">
            {plan ? (
              <>
                <Link to={`/app/clients/${plan.client}`} className="font-medium text-primary-600 hover:text-primary-700">
                  {plan.client_name}
                </Link>{' '}
                | Version {plan.version_number} | {plan.status_display ?? plan.status}
              </>
            ) : (
              'Loading treatment plan...'
            )}
          </p>
        </div>
        <Link to="/app/treatment-plans" className="btn-secondary">
          Back
        </Link>
      </div>

      {query.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {getErrorMessage(query.error)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Plan Summary</h2>
            {query.isLoading && !plan ? (
              <div className="space-y-3">
                <div className="h-4 w-72 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-96 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-80 bg-gray-200 rounded animate-pulse" />
              </div>
            ) : plan ? (
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-gray-500">Effective</div>
                    <div className="text-gray-900">{plan.effective_date}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Review date</div>
                    <div className="text-gray-900">{plan.review_date}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Hours/week</div>
                    <div className="text-gray-900">{plan.recommended_hours_per_week}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Diagnosis</div>
                    <div className="text-gray-900">
                      {plan.primary_diagnosis_code} - {plan.primary_diagnosis_description}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-gray-500">Presenting concerns</div>
                  <div className="text-gray-900 whitespace-pre-wrap">{plan.presenting_concerns}</div>
                </div>
                <div>
                  <div className="text-gray-500">Clinical summary</div>
                  <div className="text-gray-900 whitespace-pre-wrap">{plan.clinical_summary}</div>
                </div>
                <div>
                  <div className="text-gray-500">Treatment recommendations</div>
                  <div className="text-gray-900 whitespace-pre-wrap">{plan.treatment_recommendations}</div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600">No plan data.</div>
            )}
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Goals & Targets</h2>
            {plan && plan.goals.length > 0 ? (
              <div className="space-y-4">
                {plan.goals.map((g) => (
                  <div key={g.id} className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium text-gray-900">
                          Goal {g.goal_number}: {g.domain}
                        </div>
                        <div className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{g.goal_description}</div>
                        <div className="mt-2 text-xs text-gray-600">
                          Baseline: {g.baseline} | Mastery: {g.mastery_criteria} | Service: {g.service_code}
                        </div>
                      </div>
                      <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                        {g.status_display ?? g.status}
                      </span>
                    </div>

                    {(g.targets?.length ?? 0) > 0 && (
                      <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="table-header">Target</th>
                              <th className="table-header">Measurement</th>
                              <th className="table-header">Mastery</th>
                              <th className="table-header">Phase</th>
                              <th className="table-header">Active</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {g.targets.map((t) => (
                              <tr key={t.id}>
                                <td className="table-cell">
                                  #{t.target_number} {t.target_description}
                                </td>
                                <td className="table-cell">{t.measurement_type_display}</td>
                                <td className="table-cell">
                                  {t.mastery_threshold}% for {t.mastery_consecutive_sessions} sessions
                                </td>
                                <td className="table-cell">{t.current_phase_display}</td>
                                <td className="table-cell">{t.is_active ? 'Yes' : 'No'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-600">No goals found.</div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Audit</h2>
            {plan ? (
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Created by</span>
                  <span className="font-medium text-gray-900">{plan.created_by_name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Approved by</span>
                  <span className="font-medium text-gray-900">{plan.approved_by_name ?? '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Approved at</span>
                  <span className="font-medium text-gray-900">{plan.approved_at ?? '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Updated</span>
                  <span className="font-medium text-gray-900">{plan.updated_at}</span>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600">No audit data.</div>
            )}
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Behavior Definitions</h2>
            {plan && plan.behavior_definitions.length > 0 ? (
              <div className="space-y-3">
                {plan.behavior_definitions.map((b) => (
                  <div key={b.id} className="rounded-lg border border-gray-200 p-3">
                    <div className="font-medium text-gray-900">{b.behavior_name}</div>
                    <div className="mt-1 text-xs text-gray-600">
                      Measurement: {b.measurement_type_display} | Target: {b.is_target_behavior ? 'Yes' : 'No'}
                    </div>
                    <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{b.operational_definition}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-600">No behavior definitions.</div>
            )}
          </div>

          <AttachmentsCard planId={planId} attachments={query.data?.attachments ?? []} onChanged={() => query.refetch?.()} />
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Attachments
// ──────────────────────────────────────────────────────────────────────

const KIND_OPTIONS = [
  { value: 'assessment', label: 'Assessment / Evaluation' },
  { value: 'iep', label: 'IEP / 504 Plan' },
  { value: 'medical', label: 'Medical Record' },
  { value: 'prior_plan', label: 'Prior Treatment Plan' },
  { value: 'progress_report', label: 'Progress Report' },
  { value: 'data', label: 'Behavior Data / Graph Source' },
  { value: 'other', label: 'Other' },
]

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

function AttachmentsCard({
  planId,
  attachments,
  onChanged,
}: {
  planId: number
  attachments: Attachment[]
  onChanged?: () => void
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [kind, setKind] = useState('other')

  const onPickFile = (f: File) => {
    setError(null)
    if (f.size > 25 * 1024 * 1024) {
      setError('File too large (25 MB max).')
      return
    }
    setPendingFile(f)
    setTitle((prev) => prev || f.name)
  }

  const submit = async () => {
    if (!pendingFile) return
    setUploading(true)
    setError(null)
    try {
      const fd = new FormData()
      fd.append('file', pendingFile)
      fd.append('title', title || pendingFile.name)
      fd.append('kind', kind)
      await apiClient.post(`/api/v1/treatment-plans/${planId}/attachments/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setPendingFile(null)
      setTitle('')
      setKind('other')
      if (fileInputRef.current) fileInputRef.current.value = ''
      onChanged?.()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setUploading(false)
    }
  }

  const remove = async (attachmentId: number) => {
    if (!confirm('Delete this attachment? This cannot be undone.')) return
    try {
      await apiClient.delete(`/api/v1/treatment-plans/${planId}/attachments/${attachmentId}/`)
      onChanged?.()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Attachments</h2>

      {/* Upload row */}
      <div
        className="rounded-md border-2 border-dashed border-gray-300 bg-gray-50 p-4 mb-4"
        onDragOver={(e) => { e.preventDefault() }}
        onDrop={(e) => {
          e.preventDefault()
          const f = e.dataTransfer.files?.[0]
          if (f) onPickFile(f)
        }}
      >
        {!pendingFile ? (
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Drag a file here or{' '}
              <button
                type="button"
                className="text-primary-600 hover:underline font-medium"
                onClick={() => fileInputRef.current?.click()}
              >
                browse
              </button>
              {' '}to attach a PDF, Word doc, image, or spreadsheet (25 MB max).
            </p>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.txt"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) onPickFile(f)
              }}
            />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-gray-700">
              Selected: <strong>{pendingFile.name}</strong> ({formatBytes(pendingFile.size)})
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600">Title</label>
                <input
                  type="text"
                  className="input mt-1"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Kind</label>
                <select className="input mt-1" value={kind} onChange={(e) => setKind(e.target.value)}>
                  {KIND_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" disabled={uploading} className="btn-primary" onClick={submit}>
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
              <button
                type="button"
                disabled={uploading}
                className="btn-secondary"
                onClick={() => { setPendingFile(null); setTitle(''); setKind('other'); if (fileInputRef.current) fileInputRef.current.value = '' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 mb-4">
          {error}
        </div>
      )}

      {/* List */}
      {attachments.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No attachments yet.</p>
      ) : (
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
          {attachments.map((a) => (
            <li key={a.id} className="flex items-center justify-between p-3 text-sm">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-gray-900 truncate">{a.title}</div>
                <div className="text-xs text-gray-500">
                  {a.kind_display} · {a.file_name} · {formatBytes(a.file_size)} · uploaded {new Date(a.uploaded_at).toLocaleDateString()}
                  {a.uploaded_by_name ? ` by ${a.uploaded_by_name}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-3 ml-4">
                {a.download_url && (
                  <a
                    href={a.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    Download
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => remove(a.id)}
                  className="text-rose-600 hover:underline"
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

